import "./style.scss";

import { Skeleton } from "@mui/material";
import classNames from "classnames";
import { isInfinity } from "helpers/utils";

interface BlockCardsProps {
    /**
     * The data needed to display the cards, composed of an array of objects with the following properties:
     *  - unit: for the value
     *  - label: to show new to the value
     *  - outOf: if there's a value to show out of the unit
     *  - evolution: the evolution of the value
     *  - evolutionLabel: the label of the evolution (% or anything)
     */
    data: {
        unit: number | string;
        label: string;
        outOf?: number | string;
        evolution?: number | string;
        evolutionLabel?: string;
    }[];
    /**
     * Whether to show a border top or not
     */
    borderTop?: boolean;
    /**
     * Whether to show a border left or not
     */
    borderLeft?: boolean;
    /**
     * Whether to show the label on the same line as the value or not
     */
    compact?: boolean;
    /**
     * Classname
     */
    className?: string;
    /**
     * Show evolution percentage (from last period)
     */
    showEvolution?: boolean;
    /**
     * Loading data status
     */
    isLoading?: boolean;
}

/**
 * This component is used to display cards with a value and a label, better used right under a header
 * @param data The data needed to display the cards, composed of an array of objects with the following properties: unit: for the value, label: to show new to the value, outOf: if there's a value to show out of the unit
 * @param borderTop Whether to show a border top or not
 * @param compact Whether to show the label on the same line as the value or not
 */
const BlockCards = ({
    data,
    borderTop = false,
    borderLeft = false,
    compact = false,
    className,
    showEvolution = true,
    isLoading = false,
}: BlockCardsProps) => {
    const cardStyle: any = {};
    const allCardStyle: any = {};
    if (borderTop) cardStyle["borderTop"] = "1px solid #eaeaea";
    if (borderLeft) allCardStyle["borderLeft"] = "1px solid #eaeaea";
    if (data?.length == 1) cardStyle["borderLeft"] = "1px solid #eaeaea";
    if (isLoading) cardStyle["borderLeft"] = "1px solid #eaeaea";

    return (
        <div className={`block-cards ${className ?? ""}`} style={allCardStyle}>
            {data &&
                !isLoading &&
                data.map((item) => (
                    <div
                        className={`block-card-element${compact ? " compact" : ""}`}
                        style={cardStyle}
                        key={item.label}
                        data-testid="block-card-element"
                    >
                        {compact && <span className="block-card-label">{item.label}</span>}
                        <div className="block-card-value">
                            <span className="block-card-unit">{item.unit}</span>
                            {item.outOf != undefined && <span className="block-card-outof">/{item.outOf}</span>}
                            {item.evolution != undefined && showEvolution && (
                                <span
                                    className={classNames({
                                        "block-card-evolution": true,
                                        plus: +item.evolution > 0,
                                        minus: +item.evolution <= 0,
                                        grey: isNaN(+item.evolution),
                                    })}
                                >
                                    {+item.evolution > 0 ? "+" : ""}
                                    {isInfinity(item.evolution) ? "∞" : isNaN(+item.evolution) ? "" : item.evolution}
                                    {!isInfinity(item.evolution) && !isNaN(+item.evolution) && item.evolutionLabel}
                                </span>
                            )}
                        </div>
                        {!compact && <span className="block-card-label">{item.label}</span>}
                    </div>
                ))}

            {/** Skeleton */}
            {isLoading && (
                <div
                    className={`block-card-element${compact ? " compact" : ""}`}
                    style={cardStyle}
                    data-testid="block-card-element"
                >
                    <Skeleton sx={{ width: "12rem" }} />
                    <Skeleton sx={{ width: "8rem", maxHeight: "2rem" }} className="skeleton-block-card-value" />
                </div>
            )}
        </div>
    );
};

export default BlockCards;
